const BASE = '/admin/api';

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const hasBody = init.body !== undefined && init.body !== null;
  const res = await fetch(BASE + path, {
    credentials: 'include',
    ...init,
    headers: {
      ...(hasBody ? { 'content-type': 'application/json' } : {}),
      ...(init.headers || {}),
    },
  });
  if (res.status === 401 && !path.endsWith('/login') && !path.endsWith('/me')) {
    window.location.assign('/admin/login');
    throw new Error('not authenticated');
  }
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const msg = data?.error || res.statusText;
    const err = new Error(msg) as Error & { details?: unknown; status?: number };
    err.details = data;
    err.status = res.status;
    throw err;
  }
  return data as T;
}

function safeJson(s: string) {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

export const api = {
  get: <T,>(p: string) => call<T>(p),
  post: <T,>(p: string, body?: unknown) =>
    call<T>(p, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T,>(p: string, body?: unknown) =>
    call<T>(p, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T,>(p: string, body?: unknown) =>
    call<T>(p, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T,>(p: string) => call<T>(p, { method: 'DELETE' }),
  upload: async <T,>(p: string, file: File, fields: Record<string, string>): Promise<T> => {
    const fd = new FormData();
    for (const [k, v] of Object.entries(fields)) fd.append(k, v);
    fd.append('file', file);
    const res = await fetch(BASE + p, { method: 'POST', credentials: 'include', body: fd });
    if (res.status === 401) {
      window.location.assign('/admin/login');
      throw new Error('not authenticated');
    }
    const text = await res.text();
    const data = text ? safeJson(text) : null;
    if (!res.ok) {
      const err = new Error(data?.error || res.statusText) as Error & { details?: unknown; status?: number };
      err.details = data;
      err.status = res.status;
      throw err;
    }
    return data as T;
  },
};

export type ClientInventory = {
  collected_at?: number;
  os?: { name?: string; version?: string; kernel?: string; arch?: string };
  uptime_seconds?: number;
  cpu?: { model?: string; cores?: number; load1?: number; load5?: number; load15?: number };
  memory?: { total?: number; free?: number; available?: number; used?: number; used_pct?: number };
  filesystems?: Array<{ mount: string; source?: string; fstype?: string; total: number; used: number; free: number; used_pct: number }>;
  network?: Array<{ iface: string; ipv4?: string; mac?: string }>;
  agent?: { host_customize_sha256?: string };
};

export type Client = {
  uuid: string;
  hostname: string | null;
  fingerprint: string | null;
  enrolled_at: number | null;
  first_seen: number | null;
  last_seen: number | null;
  last_status: string | null;
  last_error: string | null;
  notes: string;
  tags: string[];
  blocked: boolean;
  inventory: ClientInventory | null;
  inventory_at: number | null;
  agent_sha256: string | null;
  agent_sha256_at: number | null;
};

export type ActionRun = {
  id: number;
  ts: number;
  name: string;
  status: string;
  exit: number | null;
  output: string | null;
};

export type ClientDetail = Client & {
  enrolled_at: number | null;
  applied_hashes: Record<string, string>;
  last_actions: Array<{ name: string; status: string; exit: number; output_tail: string }>;
  manifest_files: Array<{ name: string; src: string; dest: string; mode: string; owner: string; sha256: string | null }>;
  drift: Array<{ dest: string; expected: string | null; applied: string | null }>;
  recent_runs: ActionRun[];
  runs_total: number;
  current_agent: CurrentAgent;
};

export type CurrentAgent = {
  script: { sha256: string; file: string } | null;
  service: { sha256: string; file: string } | null;
  timer: { sha256: string; file: string } | null;
};

export type ClientsListResponse = {
  clients: Client[];
  current_agent: CurrentAgent;
};

export type FileMeta = {
  id: number;
  path: string;
  sha256: string;
  size: number;
  mime: string | null;
  uploaded_at: number;
  uploaded_by: string | null;
};

export type EnrollmentToken = {
  id: string;
  masked: string;
  created_at: number;
  expires_at: number | null;
  single_use: boolean;
  used_at: number | null;
  used_by_uuid: string | null;
  note: string;
  allow_rebind: boolean;
  status: 'active' | 'used' | 'expired';
};

export type IssuedEnrollmentToken = {
  id: string;
  token: string;
  single_use: boolean;
  expires_at: number | null;
  note: string;
  allow_rebind: boolean;
  install_url: string;
  note_to_admin: string;
};

export type AuditEntry = {
  id: number;
  ts: number;
  action: string;
  target: string | null;
  actor: string | null;
  details: unknown;
};
